import React, { useState, useRef, useEffect } from 'react';
import { useFormsQuery, useBookingFormQuery } from '~/lib/Queries';
import Select from '~/components/Select';
import Loading from '~/components/Loading';
import AssistantChatConversation from './AssistantChatConversation';

const AssistantFormEdit = () => {
    const { data: formsData, isLoading: isLoadingForms } = useFormsQuery();
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const conversationRef = useRef(null);
    const iframeRef = useRef(null);

    // Prepare form options for the dropdown
    const formOptions = {};
    if (formsData) {
        formsData.forEach(form => {
            formOptions[form.id] = form.is_default_booking
                ? `${form.name} (Default Booking Form)`
                : form.name;
        });
    }

    // Get the selected form's public URL
    const getFormUrl = () => {
        if (!selectedFormId || !formsData) return null;

        const selectedForm = formsData.find(f => f.id === parseInt(selectedFormId));
        if (!selectedForm) return null;

        return selectedForm.public_url;
    };

    const formUrl = getFormUrl();

    // Force iframe reload when form selection changes
    useEffect(() => {
        if (formUrl) {
            setIsIframeLoading(true);
            setIframeKey(prev => prev + 1);
        }
    }, [formUrl]);

    // Handle messages update callback from conversation component
    const handleMessagesUpdate = (newMessages, isFinal) => {
        // Check for tool results with form_view_id
        if (newMessages && Array.isArray(newMessages)) {
            // Find the last tool message
            const toolMessages = newMessages.filter(m => m.role === 'tool');
            if (toolMessages.length > 0) {
                const lastToolMessage = toolMessages[toolMessages.length - 1];

                // Check if the tool message has content array
                if (Array.isArray(lastToolMessage.content)) {
                    for (const toolResult of lastToolMessage.content) {
                        if (toolResult.result && typeof toolResult.result === 'string') {
                            try {
                                const result = JSON.parse(toolResult.result);
                                // Check if result has a message field that needs parsing
                                if (result && typeof result === 'object') {
                                    let formViewId = result.form_view_id || result.view_form_id;

                                    // Some results have a nested message field that needs parsing
                                    if (!formViewId && result.message && typeof result.message === 'string') {
                                        try {
                                            const resultMessage = JSON.parse(result.message);
                                            formViewId = resultMessage?.view_form_id || resultMessage?.form_view_id;
                                        } catch (innerErr) {
                                            // Message field is not JSON, skip
                                        }
                                    }

                                    if (formViewId) {
                                        setSelectedFormId(formViewId);
                                    }
                                }
                            } catch (err) {
                                // Not valid JSON, skip silently
                                console.debug('Tool result parsing error:', err);
                            }
                        }
                    }
                }
            }
        }

        if (isFinal) {
            // Send postMessage to iframe to invalidate its queries
            // Use window.location.origin to ensure we're only sending to our own domain
            const targetOrigin = window.location.origin;
            console.log('Sending invalidate-queries to iframe', iframeRef.current?.contentWindow);
            iframeRef.current?.contentWindow?.postMessage('invalidate-queries', '*');
        }
    };

    if (isLoadingForms) {
        return <Loading className="py-4" />;
    }

    // Show centered modal when no form is selected
    if (!selectedFormId) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-opacity-50 z-50 p-4">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[600px] flex flex-col overflow-hidden my-auto px-4">
                    <AssistantChatConversation
                        ref={conversationRef}
                        showChat={true}
                        isNewBusiness={false}
                        hideRecentConvos={true}
                        onMessagesUpdate={handleMessagesUpdate}
                        isSideColumn={false}
                        context={null}
                        source="AssistantFormEdit"
                    />
                </div>
            </div>
        );
    }

    // Show full screen layout with form preview when form is selected
    return (
        <div className="flex h-screen overflow-hidden mt-0 rounded-t-[23px]">
            {/* Left side - Chatbox (1/3 width) */}
            <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white overflow-hidden justify-end px-4">
                <AssistantChatConversation
                    ref={conversationRef}
                    showChat={true}
                    isNewBusiness={false}
                    hideRecentConvos={true}
                    onMessagesUpdate={handleMessagesUpdate}
                    isSideColumn={true}
                    context={selectedFormId ? `Currently previewing form ID=${selectedFormId}` : null}
                    source="AssistantFormEdit"
                />
            </div>

            {/* Right side - Form Preview (2/3 width) */}
            <div className="w-2/3 flex flex-col bg-gray-50">
                {/* Form selector at the top */}
                <div className="p-4 bg-white border-b border-gray-200">
                    <Select
                        label="Select Form to Preview"
                        options={formOptions}
                        value={selectedFormId}
                        onChange={setSelectedFormId}
                        inputClassName="w-full max-w-md"
                    />
                </div>

                {/* Form preview iframe */}
                <div className="flex-1 overflow-hidden p-4 relative">
                    {isIframeLoading && formUrl && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                            <Loading message="Loading form preview..." />
                        </div>
                    )}
                    {formUrl ? (
                        <iframe
                            ref={iframeRef}
                            key={iframeKey}
                            src={formUrl}
                            className="w-full h-full border-0 rounded-lg shadow-lg bg-white"
                            title="Form Preview"
                            allow="payment"
                            onLoad={() => setIsIframeLoading(false)}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-gray-500">Select a form to preview</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssistantFormEdit;

